import { createSlice, createSelector } from "@reduxjs/toolkit";
import { isFeatureEnabled } from "../../utilities/featureFlags";

const initialState = {
    // Step 1 - Meeting Details
    step1: {
        meetingTitle: "",
        // Physical while virtual meetings are switched off, so the one remaining
        // radio option starts selected. Every resetStep1 / resetMeetingHub path
        // reads this, so it is the only place the default needs to change.
        meetingType: isFeatureEnabled("virtualMeetings") ? "virtual" : "physical",
        date: "",
        startTime: "",
        endTime: "",
        meetingLink: "",
        isLinkGenerated: 0,
        address: "",
    },
    // Step 2 - Attendees
    step2: {
        internalAttendees: [],
        externalAttendees: [],
    },
    // Step 3 - Agenda Details
    step3: {
        agendaType: "freeText", // "freeText" or "bulletPoints"
        freeText: "",
        bulletPoints: [], // Array of strings
    },
    // Current active step (0 = homepage, 1+ = form steps)
    currentStep: 0,
    // Loading and error states
    loading: false,
    error: null,
};

export const meetingHubSlice = createSlice({
    name: "meetingHub",
    initialState,
    reducers: {
        // Step 1 actions
        updateStep1Field: (state, action) => {
            const { field, value } = action.payload;
            state.step1[field] = value;
        },
        setStep1Data: (state, action) => {
            state.step1 = { ...state.step1, ...action.payload };
        },
        // Step 2 actions
        updateStep2Field: (state, action) => {
            const { field, value } = action.payload;
            state.step2[field] = value;
        },
        setStep2Data: (state, action) => {
            state.step2 = { ...state.step2, ...action.payload };
        },
        addInternalAttendee: (state, action) => {
            const attendee = action.payload;
            const id = attendee?.uid || attendee?.id;
            const exists = state.step2.internalAttendees.some((a) => (a?.uid || a?.id) === id);
            if (!exists) {
                state.step2.internalAttendees.push(attendee);
            }
        },
        removeInternalAttendee: (state, action) => {
            const attendeeId = action.payload;
            state.step2.internalAttendees = state.step2.internalAttendees.filter(
                (a) => (a?.uid || a?.id) !== attendeeId
            );
        },
        addExternalAttendee: (state, action) => {
            const attendee = action.payload;
            const id = attendee?.uid || attendee?.id;
            const exists = state.step2.externalAttendees.some((a) => (a?.uid || a?.id) === id);
            if (!exists) {
                state.step2.externalAttendees.push(attendee);
            }
        },
        removeExternalAttendee: (state, action) => {
            const attendeeId = action.payload;
            state.step2.externalAttendees = state.step2.externalAttendees.filter(
                (a) => (a?.uid || a?.id) !== attendeeId
            );
        },
        // Step 3 actions
        updateStep3Field: (state, action) => {
            const { field, value } = action.payload;
            state.step3[field] = value;
        },
        setStep3Data: (state, action) => {
            state.step3 = { ...state.step3, ...action.payload };
        },
        addBulletPoint: (state, action) => {
            const bulletText = action.payload;
            if (bulletText && bulletText.trim() !== "") {
                state.step3.bulletPoints.push(bulletText.trim());
            }
        },
        removeBulletPoint: (state, action) => {
            const index = action.payload;
            state.step3.bulletPoints = state.step3.bulletPoints.filter((_, i) => i !== index);
        },
        setAgendaType: (state, action) => {
            state.step3.agendaType = action.payload;
        },
        // Navigation actions
        setCurrentStep: (state, action) => {
            state.currentStep = action.payload;
        },
        nextStep: (state) => {
            if (state.currentStep < 3) {
                state.currentStep += 1;
            }
        },
        prevStep: (state) => {
            if (state.currentStep > 1) {
                state.currentStep -= 1;
            }
        },
        // Loading and error states
        setMeetingHubLoading: (state, action) => {
            state.loading = action.payload;
        },
        setMeetingHubError: (state, action) => {
            state.error = action.payload;
        },
        // Reset actions
        resetStep1: (state) => {
            state.step1 = initialState.step1;
        },
        resetStep2: (state) => {
            state.step2 = initialState.step2;
        },
        resetStep3: (state) => {
            state.step3 = initialState.step3;
        },
        resetMeetingHub: () => initialState,
    },
});

export const {
    updateStep1Field,
    setStep1Data,
    updateStep2Field,
    setStep2Data,
    addInternalAttendee,
    removeInternalAttendee,
    addExternalAttendee,
    removeExternalAttendee,
    updateStep3Field,
    setStep3Data,
    addBulletPoint,
    removeBulletPoint,
    setAgendaType,
    setCurrentStep,
    nextStep,
    prevStep,
    setMeetingHubLoading,
    setMeetingHubError,
    resetStep1,
    resetStep2,
    resetStep3,
    resetMeetingHub,
} = meetingHubSlice.actions;

// Selectors
export const selectStep1Data = (state) => state.meetingHub.step1;
export const selectStep2Data = (state) => state.meetingHub.step2;
export const selectInternalAttendees = (state) => state.meetingHub.step2.internalAttendees;
export const selectExternalAttendees = (state) => state.meetingHub.step2.externalAttendees;
export const selectStep3Data = (state) => state.meetingHub.step3;
export const selectAgendaType = (state) => state.meetingHub.step3.agendaType;
export const selectFreeText = (state) => state.meetingHub.step3.freeText;
export const selectBulletPoints = (state) => state.meetingHub.step3.bulletPoints;
export const selectCurrentStep = (state) => state.meetingHub.currentStep;
export const selectMeetingHubLoading = (state) => state.meetingHub.loading;
export const selectMeetingHubError = (state) => state.meetingHub.error;
export const selectAllMeetingData = createSelector(
    [selectStep1Data, selectStep2Data, selectStep3Data],
    (step1, step2, step3) => ({
        ...step1,
        ...step2,
        ...step3,
    })
);

export default meetingHubSlice.reducer;
